import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { TextField } from '@openzeppelin/ui-components';
import { Button } from '@openzeppelin/ui-components';
import { adapter } from '../data/mockAdapter';
import type { CfModuleWhitelistEntry } from '@ascend/mocked-shared/mock-data';
import { EXISTING_REGISTRIES, getRegistry } from '../data/identityRegistries';
import { getModuleConfig } from '../data/moduleConfigs';
import { Icon } from '@ascend/shared/components';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CollateralToken {
  symbol: string;
  address: string;
  haircutPercentage: string;
  liquidationThreshold: string;
  minHealthFactor: string;
}

interface SettlementToken {
  symbol: string;
  address: string;
}

interface WizardFormValues {
  name: string;
  oracleProvider: string;
  riskModule: string;
  feeModule: string;
  auctionModule: string;
  valuationModule: string;
  liquidityModule: string;
  // Identity Registry binding for this CF.
  // 'create-new' = define a new dedicated IR (the policy fields below apply).
  // any other value = reuse an existing IR (kycRequired/jurisdiction/claim*
  // come from the registry's CTR and are display-only in the wizard).
  identityRegistry: string;
  // Used only when identityRegistry === 'create-new':
  kycRequired: boolean;
  jurisdiction: string;
  claimPartnerMembership: boolean;
  claimCorporateEmail: boolean;
  claimQualifiedPremium: boolean;
  ownerAddress: string;
  ddfOpenPermissionless: boolean;
  ddfSearcherBountyBps: string;
  ddfReserveRatioBps: string;
  ddfLolrEnabled: boolean;
  ddfLolrDeadlineDays: string;
  reserveInitialAmount: string;
  reserveTargetCoverage: string;
  reserveContributionRate: string;
  reserveAutoReplenish: boolean;
  collateralTokens: CollateralToken[];
  settlementTokens: SettlementToken[];
  // The CF's attached IModularCompliance contract per ERC-3643/SEP-0057.
  // Single attachment; the runtime rules it enforces (caps, frequency limits,
  // jurisdiction gates, etc.) are bundled internally and surfaced read-only
  // via ModuleConfigPreview. Sits alongside Risk/Fee/Auction/etc. in the
  // Modules step.
  complianceModule: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ALL_STEPS = ['Facility', 'Modules', 'Identity Registry', 'Access Control', 'DDF Policy', 'Reserve Pool', 'Collateral', 'Settlement', 'Review'];

type ModuleOption = { value: string; label: string };
type ModuleOptionsMap = Record<string, ModuleOption[]>;
type ModuleDescriptionsMap = Record<string, Record<string, string>>;

const JURISDICTION_OPTIONS = [
  { value: 'us', label: 'United States' },
  { value: 'eu', label: 'European Union' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'sg', label: 'Singapore' },
  { value: 'hk', label: 'Hong Kong' },
  { value: 'ch', label: 'Switzerland' },
  { value: 'ae', label: 'UAE' },
  { value: 'global', label: 'Global (no restriction)' },
];

const COLLATERAL_TOKEN_MAP: Record<string, string> = {
  FMM: '0x1234567890123456789012345678901234567890',
  ABN: '0x2345678901234567890123456789012345678901',
  APX: '0x3456789012345678901234567890123456789012',
  SNG: '0x4567890123456789012345678901234567890123',
  MSM: '0x5678901234567890123456789012345678901234',
};

const CHAINLINK_FEED_MAP: Record<string, string> = {
  FMM: '0x9Ce2388a0c7E2B4EF420C3bC5f44c4623C507a05',
  ABN: '0x3E7d1eAB13ad0104d2750B8863b489D65364e32D',
  APX: '0xBFC28Ee6cF852b42dA69F7A5E3fEb2b14E4b3C21',
  SNG: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419',
  MSM: '0x7bAC85A8a13A4BcD8abb3eB7d6b4d632c5a57676',
};

const SETTLEMENT_TOKEN_MAP: Record<string, string> = {
  USDC: '0xA0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
  USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  EURC: '0x1aBaEA1f7C830bD89Acc67eC4af516284b1bC33c',
};

const DEPLOY_MESSAGES = [
  'Deploying Facility Proxy...',
  'Bootstrapping Modules...',
  'Configuring Registries...',
  'Setting up Compliance...',
  'Whitelisting Collateral...',
  'Whitelisting Stablecoins...',
  'Finalizing Setup...',
];

const DEFAULT_VALUES: WizardFormValues = {
  name: 'Orion Prime Credit Facility',
  oracleProvider: '',
  riskModule: '',
  feeModule: '',
  auctionModule: '',
  valuationModule: '',
  liquidityModule: '',
  kycRequired: false,
  jurisdiction: '',
  claimPartnerMembership: false,
  claimCorporateEmail: false,
  claimQualifiedPremium: false,
  identityRegistry: 'ascend-default',
  ownerAddress: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0',
  ddfOpenPermissionless: true,
  ddfSearcherBountyBps: '50',
  ddfReserveRatioBps: '1000',
  ddfLolrEnabled: true,
  ddfLolrDeadlineDays: '7',
  reserveInitialAmount: '100000',
  reserveTargetCoverage: '10',
  reserveContributionRate: '2',
  reserveAutoReplenish: true,
  collateralTokens: [
    {
      symbol: 'FMM',
      address: '0x1234567890123456789012345678901234567890',
      haircutPercentage: '25',
      liquidationThreshold: '160',
      minHealthFactor: '1.1',
    },
  ],
  settlementTokens: [
    {
      symbol: 'USDC',
      address: '0xA0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    },
  ],
  complianceModule: '',
};

// Sentinel value for the Identity Registry dropdown indicating "deploy a new
// dedicated IR for this CF". Any other value matches an EXISTING_REGISTRIES.id.
const NEW_REGISTRY_SENTINEL = 'create-new';

const CUSTOM_CLAIMS = [
  { field: 'claimPartnerMembership' as const, label: 'Partner Membership', description: 'Verified partner organization member' },
  { field: 'claimCorporateEmail' as const, label: 'Corporate Email Verified', description: 'Verified corporate domain email' },
  { field: 'claimQualifiedPremium' as const, label: 'Qualified Premium Investor', description: 'Premium investor accreditation' },
];

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

// ─── Stepper ─────────────────────────────────────────────────────────────────

function WizardStepper({ steps, currentStep }: { steps: string[]; currentStep: number }) {
  return (
    <div className="space-y-3">
      <div className="flex items-start">
        {steps.map((step, i) => {
          const isCompleted = i < currentStep;
          const isCurrent = i === currentStep;
          return (
            <Fragment key={step}>
              <div className="flex flex-col items-center gap-2 shrink-0">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <Icon name="streamline:check" className="h-5 w-5" />
                  ) : isCurrent ? (
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-5 w-5 animate-spin" />
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={`text-xs font-medium whitespace-nowrap ${
                    isCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {step}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`mt-5 mx-1 h-0.5 flex-1 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`}
                />
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="h-1 w-full rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-primary transition-all duration-300"
          style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
        />
      </div>
    </div>
  );
}

// ─── Toggle Switch ───────────────────────────────────────────────────────────

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Labeled Number Input ────────────────────────────────────────────────────

function NumberInput({
  label,
  helperText,
  ...props
}: { label: string; helperText?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input type="number" className={INPUT_CLASS} {...props} />
      {helperText && <p className="mt-1.5 text-sm text-muted-foreground">{helperText}</p>}
    </div>
  );
}

// ─── Step 0: Facility ────────────────────────────────────────────────────────

function FacilityStep({ control }: { control: Control<WizardFormValues> }) {
  return (
    <div>
      <h3 className="text-lg font-bold">Facility Details</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Initialize the basic parameters for your new Credit Facility.
      </p>
      <div className="mt-6">
        <TextField
          id="facilityName"
          label="Facility Name"
          placeholder="e.g. USDC Core Facility"
          control={control}
          name="name"
          validation={{ required: true }}
          width="half"
        />
      </div>
    </div>
  );
}

// ─── Step 1: Modules ─────────────────────────────────────────────────────────

function ModuleConfigPreview({ moduleId }: { moduleId: string }) {
  const cfg = getModuleConfig(moduleId);
  if (!cfg) return null;
  return (
    <dl className="space-y-1.5 text-sm tabular-nums">
      {cfg.fields.map((f) => (
        <div
          key={f.label}
          className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-1.5 last:border-0 last:pb-0"
        >
          <dt className="text-muted-foreground" title={f.hint}>
            {f.label}
          </dt>
          <dd className="font-medium text-right">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ModuleConfigEmpty() {
  return (
    <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border/60 px-4 py-8 text-center text-xs italic text-muted-foreground">
      Pick a module to see what it enforces.
    </div>
  );
}

// Each module is its own bordered card. Title at top, then 2-col grid:
// dropdown + description on the left, config preview on the right.
function ModuleCard({
  label,
  selected,
  description,
  children,
}: {
  label: string;
  selected: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <h4 className="mb-4 text-base font-semibold">{label}</h4>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-8">
        <div className="space-y-2">
          {children}
          {description && (
            <p className="text-sm text-ascend-primary">{description}</p>
          )}
        </div>
        <div className="md:border-l md:border-border/60 md:pl-8">
          {selected ? <ModuleConfigPreview moduleId={selected} /> : <ModuleConfigEmpty />}
        </div>
      </div>
    </div>
  );
}

function ModulesStep({
  register,
  control,
  moduleOptions,
  moduleDescriptions,
}: {
  register: ReturnType<typeof useForm<WizardFormValues>>['register'];
  control: Control<WizardFormValues>;
  moduleOptions: ModuleOptionsMap;
  moduleDescriptions: ModuleDescriptionsMap;
}) {
  const risk = useWatch({ control, name: 'riskModule' }) ?? '';
  const fee = useWatch({ control, name: 'feeModule' }) ?? '';
  const auction = useWatch({ control, name: 'auctionModule' }) ?? '';
  const valuation = useWatch({ control, name: 'valuationModule' }) ?? '';
  const liquidity = useWatch({ control, name: 'liquidityModule' }) ?? '';
  const compliance = useWatch({ control, name: 'complianceModule' }) ?? '';

  const riskLabel = (moduleOptions.Risk ?? []).find((o) => o.value === risk)?.label ?? '';
  const feeLabel = (moduleOptions.Fee ?? []).find((o) => o.value === fee)?.label ?? '';
  const auctionLabel = (moduleOptions.Auction ?? []).find((o) => o.value === auction)?.label ?? '';
  const valuationLabel = (moduleOptions.Valuation ?? []).find((o) => o.value === valuation)?.label ?? '';
  const liquidityLabel = (moduleOptions.Liquidity ?? []).find((o) => o.value === liquidity)?.label ?? '';
  const complianceLabel = (moduleOptions.Compliance ?? []).find((o) => o.value === compliance)?.label ?? '';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold">Bootstrap Configuration</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Select the core modules that will power this facility.
        </p>
      </div>

      <ModuleCard
        label="Risk Module"
        selected={risk}
        description={moduleDescriptions.Risk?.[risk] ?? ''}
      >
        <select id="riskModule" className={INPUT_CLASS} {...register('riskModule', { required: true })}>
          {(moduleOptions.Risk ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </ModuleCard>

      <ModuleCard
        label="Auction Module"
        selected={auction}
        description={moduleDescriptions.Auction?.[auction] ?? ''}
      >
        <select id="auctionModule" className={INPUT_CLASS} {...register('auctionModule', { required: true })}>
          {(moduleOptions.Auction ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </ModuleCard>

      <ModuleCard
        label="Valuation Module"
        selected={valuation}
        description={moduleDescriptions.Valuation?.[valuation] ?? ''}
      >
        <select id="valuationModule" className={INPUT_CLASS} {...register('valuationModule')}>
          <option value="">None</option>
          {(moduleOptions.Valuation ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </ModuleCard>

      <ModuleCard
        label="Liquidity Module"
        selected={liquidity}
        description={moduleDescriptions.Liquidity?.[liquidity] ?? ''}
      >
        <select id="liquidityModule" className={INPUT_CLASS} {...register('liquidityModule')}>
          <option value="">None</option>
          {(moduleOptions.Liquidity ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </ModuleCard>

      <ModuleCard
        label="Fee Module"
        selected={fee}
        description={moduleDescriptions.Fee?.[fee] ?? ''}
      >
        <select id="feeModule" className={INPUT_CLASS} {...register('feeModule', { required: true })}>
          {(moduleOptions.Fee ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </ModuleCard>

      <ModuleCard
        label="Compliance Module"
        selected={compliance}
        description={moduleDescriptions.Compliance?.[compliance] ?? ''}
      >
        <select id="complianceModule" className={INPUT_CLASS} {...register('complianceModule', { required: true })}>
          <option value="">Select a compliance module...</option>
          {(moduleOptions.Compliance ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </ModuleCard>

      <div className="rounded-lg bg-muted/40 p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Selected Module Stack
        </p>
        <div className="flex flex-wrap gap-2">
          {['Chainlink (Oracle)', riskLabel, feeLabel, auctionLabel, valuationLabel, liquidityLabel, complianceLabel].filter(Boolean).map((label) => (
            <span key={label} className="rounded-full bg-foreground px-3 py-1 text-xs font-medium text-background">
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Compliance ──────────────────────────────────────────────────────

function IdentityRegistryStep({
  control,
  register,
  watch,
}: {
  control: Control<WizardFormValues>;
  register: ReturnType<typeof useForm<WizardFormValues>>['register'];
  watch: ReturnType<typeof useForm<WizardFormValues>>['watch'];
}) {
  const selectedRegistryId = watch('identityRegistry');
  const isCreatingNew = selectedRegistryId === NEW_REGISTRY_SENTINEL;
  const reusedRegistry = !isCreatingNew ? getRegistry(selectedRegistryId) : undefined;
  const kycEnabled = watch('kycRequired');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold">Identity Registry</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Bind this credit facility to an Identity Registry — the gate that decides
          which wallets may use it based on their OnchainID claims.
        </p>
      </div>

      {/* Identity Registry picker */}
      <div className="rounded-xl border border-border p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium">Identity Registry</label>
          <p className="text-xs text-muted-foreground mb-2">
            Each credit facility binds to exactly one IR. Reusing an existing registry
            inherits its claim policy unchanged. Creating a new dedicated registry lets
            you define a fresh policy below.
          </p>
        </div>
        <select {...register('identityRegistry')} className={INPUT_CLASS}>
          {EXISTING_REGISTRIES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} — shared with {r.sharedWith} facilit{r.sharedWith === 1 ? 'y' : 'ies'}
            </option>
          ))}
          <option value={NEW_REGISTRY_SENTINEL}>Create new registry dedicated to this facility</option>
        </select>

        {/* Mode A: read-only preview of reused IR */}
        {reusedRegistry && (
          <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <Icon name="streamline:shield-check" className="h-4 w-4 text-muted-foreground" />
                  Inherited policy
                </h4>
                <p className="text-xs text-muted-foreground">{reusedRegistry.description}</p>
              </div>
              <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                Read-only
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="block uppercase tracking-wider text-muted-foreground">Jurisdiction</span>
                <span className="font-medium">{reusedRegistry.jurisdiction}</span>
              </div>
              <div>
                <span className="block uppercase tracking-wider text-muted-foreground">Trusted issuer</span>
                <span className="font-mono font-medium">
                  Ascend · {reusedRegistry.trustedIssuer.slice(0, 6)}…{reusedRegistry.trustedIssuer.slice(-4)}
                </span>
              </div>
            </div>
            <div>
              <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Required claim topics</span>
              <div className="space-y-1.5">
                {reusedRegistry.claimTopics.map((c) => (
                  <div key={c.label} className="flex items-center justify-between rounded-md bg-background/60 px-2.5 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium">{c.label}</span>
                      {c.ascendSpecific && (
                        <span className="rounded-full bg-indigo-100 px-1.5 py-0 text-[9px] font-medium uppercase tracking-wider text-indigo-700">
                          Ascend
                        </span>
                      )}
                    </div>
                    <span className={`rounded-full px-1.5 py-0 text-[10px] font-medium ${c.required ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {c.required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mode B: editable policy for new dedicated IR */}
        {isCreatingNew && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-amber-900">New registry policy</h4>
              <p className="text-xs text-amber-800">
                These fields define the new IR&apos;s CTR (claim topics) and IR-level fields.
                After deployment they become read-only and apply to every CF that later
                binds to this registry.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg bg-background p-3">
              <div>
                <p className="text-sm font-medium">KYC Required</p>
                <p className="text-xs text-muted-foreground">Mandate KYC verified claim on user OnchainID</p>
              </div>
              <Controller
                control={control}
                name="kycRequired"
                render={({ field }) => <ToggleSwitch checked={field.value} onChange={field.onChange} />}
              />
            </div>

            {kycEnabled && (
              <div className="rounded-lg bg-background p-3">
                <label htmlFor="jurisdiction" className="block text-sm font-medium mb-1.5">Jurisdiction filter</label>
                <select id="jurisdiction" className={INPUT_CLASS} {...register('jurisdiction')}>
                  <option value="">Select jurisdiction...</option>
                  {JURISDICTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  IR-level field per spec §5 (denormalised cache for cheap reads)
                </p>
              </div>
            )}

            <div className="rounded-lg bg-background p-3 space-y-2">
              <p className="text-sm font-medium">Custom claim topics</p>
              <p className="text-xs text-muted-foreground">Ascend-specific claims required by this IR (CTR contents)</p>
              {CUSTOM_CLAIMS.map((claim) => (
                <label key={claim.field} className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register(claim.field)}
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div>
                    <p className="text-sm font-medium">{claim.label}</p>
                    <p className="text-xs text-muted-foreground">{claim.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Facility ONCHAINID */}
      <div className="rounded-xl border border-border p-5">
        <h4 className="text-base font-semibold">Facility ONCHAINID</h4>
        <p className="mt-1 text-sm text-muted-foreground">
          On deploy, the platform mints a new OnchainID for this facility via
          <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">IdFactory.createTokenIdentity</code>
          so it can hold and receive ERC-3643 collateral.
        </p>
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1.5">Predicted address</label>
          <input
            className={`${INPUT_CLASS} bg-muted cursor-not-allowed font-mono text-xs`}
            value="0x7F2c4aE9D1b3F5c8d0A6e7B4f2C9a1D3E5f7A8aB4"
            readOnly
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Each accepted collateral token&apos;s IR must whitelist this address before deposits succeed.
          </p>
        </div>
      </div>

    </div>
  );
}

// ─── Step 3: Access Control (admin role grants) ──────────────────────────────

function AccessControlStep({
  control,
  operatorAddresses,
  setOperatorAddresses,
  liquidatorAddresses,
  setLiquidatorAddresses,
}: {
  control: Control<WizardFormValues>;
  operatorAddresses: string[];
  setOperatorAddresses: React.Dispatch<React.SetStateAction<string[]>>;
  liquidatorAddresses: string[];
  setLiquidatorAddresses: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold">Access Control</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Bootstrap admin role membership at deploy. Ongoing role management lives on
          the facility&apos;s Access Control tab post-deploy.
        </p>
      </div>

      {/* Owner */}
      <div className="rounded-xl border border-border p-5">
        <h5 className="font-semibold">OWNER Role</h5>
        <p className="text-sm text-muted-foreground">Full administrative control (caller by default)</p>
        <div className="mt-3">
          <TextField
            id="ownerAddress"
            label="Owner Address"
            placeholder="0x..."
            control={control}
            name="ownerAddress"
            helperText="Defaults to deployer address"
          />
        </div>
      </div>

      {/* Operator */}
      <div className="rounded-xl border border-border p-5">
        <h5 className="font-semibold">OPERATOR Role</h5>
        <p className="text-sm text-muted-foreground">
          Can manage facility operations (caller by default + additional addresses)
        </p>
        {operatorAddresses.map((addr, i) => (
          <div key={i} className="mt-2 flex items-center gap-2">
            <input
              className={INPUT_CLASS}
              placeholder="0x..."
              value={addr}
              onChange={(e) => {
                const next = [...operatorAddresses];
                next[i] = e.target.value;
                setOperatorAddresses(next);
              }}
            />
            <button
              type="button"
              onClick={() => setOperatorAddresses((prev) => prev.filter((_, j) => j !== i))}
              className="rounded-md p-2 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setOperatorAddresses((prev) => [...prev, ''])}
        >
          <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" /> Add Operator Address
        </Button>
      </div>

      {/* Liquidator */}
      <div className="rounded-xl border border-border p-5">
        <h5 className="font-semibold">LIQUIDATOR Role</h5>
        <p className="text-sm text-muted-foreground">
          DDF contract addresses authorized to trigger liquidations
        </p>
        {liquidatorAddresses.map((addr, i) => (
          <div key={i} className="mt-2 flex items-center gap-2">
            <input
              className={INPUT_CLASS}
              placeholder="0x..."
              value={addr}
              onChange={(e) => {
                const next = [...liquidatorAddresses];
                next[i] = e.target.value;
                setLiquidatorAddresses(next);
              }}
            />
            <button
              type="button"
              onClick={() => setLiquidatorAddresses((prev) => prev.filter((_, j) => j !== i))}
              className="rounded-md p-2 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setLiquidatorAddresses((prev) => [...prev, ''])}
        >
          <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" /> Add DDF Contract
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: DDF Policy ──────────────────────────────────────────────────────

function DdfPolicyStep({
  control,
  register,
  watch,
  bidderWhitelistAddresses,
  setBidderWhitelistAddresses,
}: {
  control: Control<WizardFormValues>;
  register: ReturnType<typeof useForm<WizardFormValues>>['register'];
  watch: ReturnType<typeof useForm<WizardFormValues>>['watch'];
  bidderWhitelistAddresses: string[];
  setBidderWhitelistAddresses: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const openPerm = watch('ddfOpenPermissionless');
  const lolrEnabled = watch('ddfLolrEnabled');
  const bounty = watch('ddfSearcherBountyBps');
  const reserveRatio = watch('ddfReserveRatioBps');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold">DDF Policy Configuration</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure the Distressed Debt Facility reserve and bidder parameters.
        </p>
      </div>

      {/* Liquidation Trigger Access */}
      <div className="rounded-xl border border-border p-5 space-y-4">
        <h4 className="font-semibold">Liquidation Trigger Access</h4>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Open Permissionless</p>
            <p className="text-sm text-muted-foreground">Allow any address to trigger liquidations</p>
          </div>
          <Controller
            control={control}
            name="ddfOpenPermissionless"
            render={({ field }) => <ToggleSwitch checked={field.value} onChange={field.onChange} />}
          />
        </div>
        {openPerm && (
          <NumberInput
            label="Searcher Bounty (basis points)"
            helperText="Finder's fee paid to liquidation trigger (e.g., 50 = 0.5%)"
            {...register('ddfSearcherBountyBps')}
          />
        )}
      </div>

      {/* Bidder Whitelist */}
      <div className="rounded-xl border border-border p-5 space-y-4">
        <h4 className="font-semibold">Bidder Whitelist</h4>
        <p className="text-sm text-muted-foreground">
          Manage whitelisted bidder addresses that can participate in DDF auctions.
          KYC requirements are automatically enforced by the facility&apos;s compliance module.
        </p>
        {bidderWhitelistAddresses.map((addr, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className={INPUT_CLASS}
              placeholder="0x..."
              value={addr}
              onChange={(e) => {
                const next = [...bidderWhitelistAddresses];
                next[i] = e.target.value;
                setBidderWhitelistAddresses(next);
              }}
            />
            <button
              type="button"
              onClick={() => setBidderWhitelistAddresses((prev) => prev.filter((_, j) => j !== i))}
              className="rounded-md p-2 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setBidderWhitelistAddresses((prev) => [...prev, ''])}
        >
          <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" /> Add Bidder Address
        </Button>
      </div>

      {/* Reserve & LOLR */}
      <div className="rounded-xl border border-border p-5 space-y-4">
        <h4 className="font-semibold">Reserve & Lender-of-Last-Resort</h4>
        <NumberInput
          label="Reserve Ratio (basis points)"
          helperText="Target reserve as % of TVL (e.g., 1000 = 10%)"
          {...register('ddfReserveRatioBps')}
        />
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Enable LOLR Mechanism</p>
            <p className="text-sm text-muted-foreground">Allow reserve to advance funds if auction fails</p>
          </div>
          <Controller
            control={control}
            name="ddfLolrEnabled"
            render={({ field }) => <ToggleSwitch checked={field.value} onChange={field.onChange} />}
          />
        </div>
        <NumberInput
          label="LOLR Settlement Deadline (days)"
          helperText="Days to replenish reserve after LOLR advance"
          {...register('ddfLolrDeadlineDays')}
        />
      </div>

      {/* DDF Policy Summary */}
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-5">
        <p className="mb-2 font-semibold text-primary">DDF Policy Summary</p>
        <ul className="space-y-1 text-sm text-primary">
          <li>
            &bull; Trigger: {openPerm ? 'Open permissionless' : 'Restricted'} with{' '}
            {(Number(bounty) / 100).toFixed(1)}% bounty
          </li>
          <li>
            &bull; Bidder Whitelist: {bidderWhitelistAddresses.filter(Boolean).length} address(es)
          </li>
          <li>
            &bull; Reserve: {(Number(reserveRatio) / 100).toFixed(0)}% target,{' '}
            {lolrEnabled ? 'LOLR enabled' : 'LOLR disabled'}
          </li>
        </ul>
      </div>
    </div>
  );
}

// ─── Step 4: Reserve Pool ───────────────────────────────────────────────────

function ReservePoolStep({
  register,
  watch,
  setValue,
}: {
  register: ReturnType<typeof useForm<WizardFormValues>>['register'];
  watch: ReturnType<typeof useForm<WizardFormValues>>['watch'];
  setValue: ReturnType<typeof useForm<WizardFormValues>>['setValue'];
}) {
  const autoReplenish = watch('reserveAutoReplenish');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold">Reserve Pool</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure the reserve pool that backstops facility obligations during DDF events.
        </p>
      </div>

      <div className="rounded-xl border border-border p-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <NumberInput
            label="Initial Reserve Amount (USD)"
            helperText="Starting reserve pool balance"
            {...register('reserveInitialAmount')}
          />
          <NumberInput
            label="Target Coverage Ratio (%)"
            helperText="Target reserve-to-exposure ratio"
            {...register('reserveTargetCoverage')}
          />
          <NumberInput
            label="Contribution Rate (%)"
            helperText="Percentage of fees routed to reserves"
            {...register('reserveContributionRate')}
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div>
            <p className="text-sm font-medium">Auto-Replenishment</p>
            <p className="text-xs text-muted-foreground">Automatically top up reserves when below target</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={autoReplenish}
            onClick={() => setValue('reserveAutoReplenish', !autoReplenish)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoReplenish ? 'bg-primary' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoReplenish ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: Collateral ──────────────────────────────────────────────────────

function CollateralStep({
  fields,
  register,
  append,
  remove,
  setValue,
  watch,
}: {
  fields: { id: string }[];
  register: ReturnType<typeof useForm<WizardFormValues>>['register'];
  append: (v: CollateralToken) => void;
  remove: (i: number) => void;
  setValue: ReturnType<typeof useForm<WizardFormValues>>['setValue'];
  watch: ReturnType<typeof useForm<WizardFormValues>>['watch'];
}) {
  return (
    <div>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold">Whitelist Collateral</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add collateral tokens and configure risk parameters (up to 10 tokens).
          </p>
        </div>
        {fields.length < 10 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              append({ symbol: '', address: '', haircutPercentage: '', liquidationThreshold: '', minHealthFactor: '' })
            }
          >
            <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" /> Add Token
          </Button>
        )}
      </div>

      <div className="mt-6 space-y-4">
        {fields.map((field, index) => (
          <div key={field.id} className="rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">Collateral Token {index + 1}</h4>
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1.5">Select Token</label>
                <select
                  className={INPUT_CLASS}
                  {...register(`collateralTokens.${index}.symbol` as const)}
                  onChange={(e) => {
                    const sym = e.target.value;
                    setValue(`collateralTokens.${index}.symbol` as const, sym);
                    if (COLLATERAL_TOKEN_MAP[sym]) {
                      setValue(`collateralTokens.${index}.address` as const, COLLATERAL_TOKEN_MAP[sym]);
                    }
                  }}
                >
                  <option value="">Select token...</option>
                  {Object.keys(COLLATERAL_TOKEN_MAP).map((sym) => (
                    <option key={sym} value={sym}>
                      {sym}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Price Feed</label>
                <input
                  className="w-full rounded-md border border-input bg-muted px-3 py-2 text-sm font-mono cursor-not-allowed text-muted-foreground"
                  value={CHAINLINK_FEED_MAP[watch(`collateralTokens.${index}.symbol` as const) as string] ?? ''}
                  readOnly
                  placeholder="Select a token to populate"
                />
                <p className="mt-1 text-xs text-muted-foreground">Chainlink data feed</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Haircut (%)</label>
                <input className={`${INPUT_CLASS} bg-muted cursor-not-allowed`} value="25" readOnly />
                <p className="mt-1 text-xs text-muted-foreground">Informed by risk module</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Liq. Threshold (%)</label>
                <input className={`${INPUT_CLASS} bg-muted cursor-not-allowed`} value="160" readOnly />
                <p className="mt-1 text-xs text-muted-foreground">Informed by risk module</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Min HF</label>
                <input className="w-full rounded-md border border-input bg-muted px-3 py-2 text-sm cursor-not-allowed" value="1.1" readOnly />
                <p className="mt-1 text-xs text-muted-foreground">Informed by risk module</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 5: Settlement ──────────────────────────────────────────────────────

const SETTLEMENT_DENOMINATION: Record<string, string[]> = {
  USD: ['USDC', 'USDT'],
  EUR: ['EURC'],
};

function SettlementStep({
  fields,
  register,
  append,
  remove,
  setValue,
}: {
  fields: { id: string }[];
  register: ReturnType<typeof useForm<WizardFormValues>>['register'];
  append: (v: SettlementToken) => void;
  remove: (i: number) => void;
  setValue: ReturnType<typeof useForm<WizardFormValues>>['setValue'];
}) {
  const [denomPerRow, setDenomPerRow] = useState<Record<number, string>>({});

  return (
    <div>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold">Settlement Tokens</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add stablecoin settlement tokens. Use the denomination filter to narrow available options.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => append({ symbol: '', address: '' })}
        >
          <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" /> Add Token
        </Button>
      </div>

      <div className="mt-4 space-y-4">
        {fields.map((field, index) => {
          const denom = denomPerRow[index] ?? 'USD';
          const allowedTokens = SETTLEMENT_DENOMINATION[denom] ?? Object.keys(SETTLEMENT_TOKEN_MAP);

          return (
          <div key={field.id} className="rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">Settlement Token {index + 1}</h4>
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Denomination</label>
                <div className="flex gap-1">
                  {Object.keys(SETTLEMENT_DENOMINATION).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => {
                        setDenomPerRow((prev) => ({ ...prev, [index]: d }));
                        setValue(`settlementTokens.${index}.symbol` as const, '');
                        setValue(`settlementTokens.${index}.address` as const, '');
                      }}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                        denom === d
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Select Stablecoin</label>
                <select
                  className={INPUT_CLASS}
                  {...register(`settlementTokens.${index}.symbol` as const)}
                  onChange={(e) => {
                    const sym = e.target.value;
                    setValue(`settlementTokens.${index}.symbol` as const, sym);
                    if (SETTLEMENT_TOKEN_MAP[sym]) {
                      setValue(`settlementTokens.${index}.address` as const, SETTLEMENT_TOKEN_MAP[sym]);
                    }
                  }}
                >
                  <option value="">Select stablecoin...</option>
                  {allowedTokens.map((sym) => (
                    <option key={sym} value={sym}>
                      {sym}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Base Interest Rate (%)</label>
                <input
                  className="w-full rounded-md border border-input bg-muted px-3 py-2 text-sm cursor-not-allowed"
                  value="6.25"
                  readOnly
                />
                <p className="mt-1 text-xs text-muted-foreground">Informed by liquidity module</p>
              </div>
            </div>

          </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 6: Review ──────────────────────────────────────────────────────────

function ReviewStep({ values, bidderWhitelistAddresses, hasDdfPolicy, moduleOptions }: { values: WizardFormValues; bidderWhitelistAddresses: string[]; hasDdfPolicy: boolean; moduleOptions: ModuleOptionsMap }) {
  const riskLabel = (moduleOptions.Risk ?? []).find((o) => o.value === values.riskModule)?.label ?? values.riskModule;
  const feeLabel = (moduleOptions.Fee ?? []).find((o) => o.value === values.feeModule)?.label ?? values.feeModule;
  const auctionLabel = (moduleOptions.Auction ?? []).find((o) => o.value === values.auctionModule)?.label ?? values.auctionModule;
  const valuationLabel = (moduleOptions.Valuation ?? []).find((o) => o.value === values.valuationModule)?.label ?? '';
  const liquidityLabel = (moduleOptions.Liquidity ?? []).find((o) => o.value === values.liquidityModule)?.label ?? '';
  const complianceLabel = (moduleOptions.Compliance ?? []).find((o) => o.value === values.complianceModule)?.label ?? '';
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold">Review Configuration</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Review all settings before executing the setup sequence.
        </p>
      </div>

      {/* Facility Details */}
      <ReviewSection title="Facility Details">
        <ReviewRow label="Facility Name" value={values.name} />
      </ReviewSection>

      {/* Modules */}
      <ReviewSection title="Modules">
        <ReviewRow label="Oracle" value="Chainlink" />
        <ReviewRow label="Risk Module" value={riskLabel} />
        <ReviewRow label="Fee Module" value={feeLabel} />
        <ReviewRow label="Auction Module" value={auctionLabel} />
        {valuationLabel && <ReviewRow label="Valuation Module" value={valuationLabel} />}
        {liquidityLabel && <ReviewRow label="Liquidity Module" value={liquidityLabel} />}
        <ReviewRow label="Compliance Module" value={complianceLabel || 'Not selected'} />
      </ReviewSection>

      {/* Identity Registry */}
      <ReviewSection title="Identity Registry">
        {values.identityRegistry === NEW_REGISTRY_SENTINEL ? (
          <>
            <ReviewRow label="Registry" value="New registry dedicated to this facility" />
            <ReviewRow label="KYC Required" value={values.kycRequired ? 'Yes' : 'No'} />
            {values.kycRequired && (
              <ReviewRow
                label="Jurisdiction"
                value={(JURISDICTION_OPTIONS.find((o) => o.value === values.jurisdiction)?.label ?? values.jurisdiction) || 'Not set'}
              />
            )}
            <ReviewRow
              label="Custom Claims"
              value={
                [
                  values.claimPartnerMembership && 'Partner Membership',
                  values.claimCorporateEmail && 'Corporate Email',
                  values.claimQualifiedPremium && 'Qualified Premium Investor',
                ]
                  .filter(Boolean)
                  .join(', ') || 'None'
              }
            />
          </>
        ) : (
          (() => {
            const reg = getRegistry(values.identityRegistry);
            return reg ? (
              <>
                <ReviewRow label="Registry" value={`${reg.name} (shared with ${reg.sharedWith})`} />
                <ReviewRow label="Jurisdiction" value={reg.jurisdiction} />
                <ReviewRow
                  label="Required claim topics"
                  value={reg.claimTopics.filter((c) => c.required).map((c) => c.label).join(', ')}
                />
              </>
            ) : (
              <ReviewRow label="Registry" value="Not selected" />
            );
          })()
        )}
        <ReviewRow label="Facility ONCHAINID" value="Auto-generated at deploy" />
      </ReviewSection>

      {/* Access Control */}
      <ReviewSection title="Access Control">
        <ReviewRow label="Owner" value={values.ownerAddress || 'Deployer (default)'} />
      </ReviewSection>

      {/* DDF Policy */}
      {hasDdfPolicy && (
        <ReviewSection title="DDF Policy">
          <ReviewRow label="Open Permissionless" value={values.ddfOpenPermissionless ? 'Yes' : 'No'} />
          <ReviewRow label="Searcher Bounty" value={`${values.ddfSearcherBountyBps} bps`} />
          <ReviewRow label="Bidder Whitelist" value={`${bidderWhitelistAddresses.filter(Boolean).length} address(es)`} />
          <ReviewRow label="Reserve Ratio" value={`${values.ddfReserveRatioBps} bps`} />
          <ReviewRow label="LOLR" value={values.ddfLolrEnabled ? `Enabled (${values.ddfLolrDeadlineDays}d deadline)` : 'Disabled'} />
        </ReviewSection>
      )}

      {/* Reserve Pool */}
      {hasDdfPolicy && (
        <ReviewSection title="Reserve Pool">
          <ReviewRow label="Initial Amount" value={`$${Number(values.reserveInitialAmount).toLocaleString()}`} />
          <ReviewRow label="Target Coverage" value={`${values.reserveTargetCoverage}%`} />
          <ReviewRow label="Contribution Rate" value={`${values.reserveContributionRate}%`} />
          <ReviewRow label="Auto-Replenish" value={values.reserveAutoReplenish ? 'Enabled' : 'Disabled'} />
        </ReviewSection>
      )}

      {/* Collateral Tokens */}
      <ReviewSection title="Collateral Tokens">
        {values.collateralTokens.map((t, i) => (
          <div key={i} className="text-sm">
            <span className="font-medium">{t.symbol || `Token ${i + 1}`}</span>
            <span className="ml-2 text-muted-foreground">
              Haircut: {t.haircutPercentage}% &middot; Liq: {t.liquidationThreshold}% &middot; Min HF:{' '}
              {t.minHealthFactor}
            </span>
            {CHAINLINK_FEED_MAP[t.symbol] && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">Feed: {CHAINLINK_FEED_MAP[t.symbol]}</p>
            )}
          </div>
        ))}
      </ReviewSection>

      {/* Settlement Tokens */}
      <ReviewSection title="Settlement Tokens">
        {values.settlementTokens.map((t, i) => (
          <div key={i} className="text-sm">
            <span className="font-medium">{t.symbol || `Token ${i + 1}`}</span>
            <span className="ml-2 text-muted-foreground font-mono">{SETTLEMENT_TOKEN_MAP[t.symbol] ?? ''}</span>
          </div>
        ))}
      </ReviewSection>

      {/* Warning */}
      <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
        <Icon name="streamline:warning-triangle" className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600" />
        <div>
          <p className="font-semibold text-yellow-800">Setup Sequence Required</p>
          <p className="mt-1 text-sm text-yellow-700">
            Clicking Deploy will initiate a multi-step transaction sequence to fully provision the facility.
            Please keep your wallet connected.
          </p>
        </div>
      </div>
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border p-5">
      <h4 className="mb-3 font-semibold">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

// ─── Deploy Progress ─────────────────────────────────────────────────────────

function DeployProgress({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-12 w-12 animate-spin text-primary" />
      <p className="mt-4 text-lg font-semibold">{DEPLOY_MESSAGES[step]}</p>
      <div className="mt-4 h-2 w-64 rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${((step + 1) / total) * 100}%` }}
        />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Step {step + 1} of {total}
      </p>
    </div>
  );
}

const MOCK_TX_HASH = '0x8a3b7d1e4f2c6a9b0d5e3f1c7b2a4d6e8f0c1a3b5d7e9f0a2b4c6d8e0f1a2b3c';
const EXPLORER_BASE_URL = 'https://etherscan.io/tx/';

function DeploySuccess({ name, onViewFacility }: { name: string; onViewFacility: () => void }) {
  const shortHash = `${MOCK_TX_HASH.slice(0, 10)}...${MOCK_TX_HASH.slice(-8)}`;

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <Icon name="streamline:check" className="h-8 w-8 text-green-600" />
      </div>
      <h2 className="mt-4 text-xl font-bold">Credit Facility Setup Complete!</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Your new Credit Facility &ldquo;{name}&rdquo; has been fully configured and deployed.
      </p>

      <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2.5">
        <span className="text-sm text-muted-foreground">Tx Hash:</span>
        <a
          href={`${EXPLORER_BASE_URL}${MOCK_TX_HASH}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-mono text-sm text-primary hover:underline"
        >
          {shortHash}
          <Icon name="streamline:share-link" className="h-3.5 w-3.5" />
        </a>
      </div>

      <Button className="mt-6" onClick={onViewFacility}>
        <Icon name="streamline:share-link" className="mr-2 h-4 w-4" /> View Created Facility
      </Button>
    </div>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export function DeployCfWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployStep, setDeployStep] = useState(0);
  const [deployComplete, setDeployComplete] = useState(false);

  const [operatorAddresses, setOperatorAddresses] = useState<string[]>([]);
  const [liquidatorAddresses, setLiquidatorAddresses] = useState<string[]>([]);
  const [bidderWhitelistAddresses, setBidderWhitelistAddresses] = useState<string[]>([]);

  const [moduleOptions, setModuleOptions] = useState<ModuleOptionsMap>({
    Oracle: [],
    Risk: [],
    Fee: [],
    Auction: [],
    Valuation: [],
    Liquidity: [],
    Compliance: [],
  });
  const [moduleDescriptions, setModuleDescriptions] = useState<ModuleDescriptionsMap>({
    Oracle: {},
    Risk: {},
    Fee: {},
    Auction: {},
    Valuation: {},
    Liquidity: {},
    Compliance: {},
  });

  const { control, register, watch, setValue, getValues } = useForm<WizardFormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const loadModulesRef = useRef(false);
  useEffect(() => {
    if (loadModulesRef.current) return;
    loadModulesRef.current = true;
    async function loadModules() {
      const [oracle, risk, fee, auction, valuation, liquidity, compliance] = await Promise.all([
        adapter.modules.listByType('Oracle'),
        adapter.modules.listByType('Risk'),
        adapter.modules.listByType('Fee'),
        adapter.modules.listByType('Auction'),
        adapter.modules.listByType('Valuation'),
        adapter.modules.listByType('Liquidity'),
        adapter.modules.listByType('Compliance'),
      ]);

      const toOptions = (entries: CfModuleWhitelistEntry[]): ModuleOption[] =>
        entries.map((m) => ({ value: m.id, label: m.name }));
      const toDescriptions = (entries: CfModuleWhitelistEntry[]): Record<string, string> =>
        Object.fromEntries(entries.map((m) => [m.id, m.description]));

      setModuleOptions({
        Oracle: toOptions(oracle),
        Risk: toOptions(risk),
        Fee: toOptions(fee),
        Auction: toOptions(auction),
        Valuation: toOptions(valuation),
        Liquidity: toOptions(liquidity),
        Compliance: toOptions(compliance),
      });
      setModuleDescriptions({
        Oracle: toDescriptions(oracle),
        Risk: toDescriptions(risk),
        Fee: toDescriptions(fee),
        Auction: toDescriptions(auction),
        Valuation: toDescriptions(valuation),
        Liquidity: toDescriptions(liquidity),
        Compliance: toDescriptions(compliance),
      });

      // Default required modules to first available option
      if (oracle.length > 0) setValue('oracleProvider', oracle[0].id);
      if (risk.length > 0) setValue('riskModule', risk[0].id);
      if (fee.length > 0) setValue('feeModule', fee[0].id);
      if (auction.length > 0) setValue('auctionModule', auction[0].id);
      if (compliance.length > 0) setValue('complianceModule', compliance[0].id);
    }
    loadModules();
  }, [setValue]);

  const hasDdfPolicy = liquidatorAddresses.filter(Boolean).length > 0;
  const steps = hasDdfPolicy
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => s !== 'DDF Policy' && s !== 'Reserve Pool');

  const {
    fields: collateralFields,
    append: appendCollateral,
    remove: removeCollateral,
  } = useFieldArray({ control, name: 'collateralTokens' });

  const {
    fields: settlementFields,
    append: appendSettlement,
    remove: removeSettlement,
  } = useFieldArray({ control, name: 'settlementTokens' });

  const currentStepName = steps[currentStep];

  function validateCurrentStep(): boolean {
    const v = getValues();
    switch (currentStepName) {
      case 'Facility':
        return v.name.trim().length > 0;
      case 'Modules':
        return !!(v.oracleProvider && v.riskModule && v.feeModule && v.auctionModule && v.complianceModule);
      case 'Collateral':
        return v.collateralTokens.length > 0 && v.collateralTokens.every((t) => t.symbol && t.address);
      case 'Settlement':
        return v.settlementTokens.length > 0 && v.settlementTokens.every((t) => t.symbol && t.address);
      default:
        return true;
    }
  }

  function handleNext() {
    if (!validateCurrentStep()) return;
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  }

  function handleBack() {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  async function handleDeploy() {
    setIsDeploying(true);
    for (let i = 0; i < DEPLOY_MESSAGES.length; i++) {
      setDeployStep(i);
      await new Promise((r) => setTimeout(r, 800));
    }
    setIsDeploying(false);
    setDeployComplete(true);
  }

  if (deployComplete) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="rounded-xl border border-border">
          <div className="p-6">
            <DeploySuccess name={getValues('name')} onViewFacility={() => navigate('/dashboard')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader />
      <WizardStepper steps={steps} currentStep={currentStep} />

      <div className="rounded-xl border border-border">
        <div className="p-6">
          {isDeploying ? (
            <DeployProgress step={deployStep} total={DEPLOY_MESSAGES.length} />
          ) : (
            <>
              {currentStepName === 'Facility' && <FacilityStep control={control} />}
              {currentStepName === 'Modules' && (
                <ModulesStep register={register} control={control} moduleOptions={moduleOptions} moduleDescriptions={moduleDescriptions} />
              )}
              {currentStepName === 'Identity Registry' && (
                <IdentityRegistryStep
                  control={control}
                  register={register}
                  watch={watch}
                />
              )}
              {currentStepName === 'Access Control' && (
                <AccessControlStep
                  control={control}
                  operatorAddresses={operatorAddresses}
                  setOperatorAddresses={setOperatorAddresses}
                  liquidatorAddresses={liquidatorAddresses}
                  setLiquidatorAddresses={setLiquidatorAddresses}
                />
              )}
              {currentStepName === 'DDF Policy' && (
                <DdfPolicyStep
                  control={control}
                  register={register}
                  watch={watch}
                  bidderWhitelistAddresses={bidderWhitelistAddresses}
                  setBidderWhitelistAddresses={setBidderWhitelistAddresses}
                />
              )}
              {currentStepName === 'Collateral' && (
                <CollateralStep
                  fields={collateralFields}
                  register={register}
                  append={appendCollateral}
                  remove={removeCollateral}
                  setValue={setValue}
                  watch={watch}
                />
              )}
              {currentStepName === 'Reserve Pool' && (
                <ReservePoolStep register={register} watch={watch} setValue={setValue} />
              )}
              {currentStepName === 'Settlement' && (
                <SettlementStep
                  fields={settlementFields}
                  register={register}
                  append={appendSettlement}
                  remove={removeSettlement}
                  setValue={setValue}
                />
              )}
              {currentStepName === 'Review' && (
                <ReviewStep
                  values={getValues()}
                  bidderWhitelistAddresses={bidderWhitelistAddresses}
                  hasDdfPolicy={hasDdfPolicy}
                  moduleOptions={moduleOptions}
                />
              )}
            </>
          )}
        </div>

        {!isDeploying && (
          <div className="flex items-center justify-between border-t border-border px-6 py-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="mr-2 h-4 w-4" /> Cancel
              </Button>
              {currentStep > 0 && (
                <Button variant="outline" onClick={handleBack}>
                  <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="mr-2 h-4 w-4" /> Back
                </Button>
              )}
            </div>
            {currentStep < steps.length - 1 ? (
              <Button onClick={handleNext}>
                Next <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleDeploy}>
                <Icon name="streamline:fireworks-rocket" className="mr-2 h-4 w-4" /> Deploy Facility
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    <div className="flex items-start gap-3">
      <Icon name="streamline:interface-edit-grid-grid-layout-layouts-module" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">Setup Credit Facility</h1>
        <p className="text-sm text-muted-foreground">
          Complete the guided workflow to deploy and configure a new Credit Facility.
        </p>
      </div>
    </div>
  );
}
